<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { ChevronRight } from "lucide-svelte"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { appStore } from "$app/store.svelte"
  import Goal from "$app/Pages/Plans/Onboarding/components/Goal.svelte"

  let props = $props()

  let {
    rootGoalId
  }: {
    rootGoalId: number
  } = props

  let loaded = $state(false)
  let errors: any = $state({})

  let nameInput: HTMLInputElement = $state(null)

  let long_term_goal: GoalType = $state(null)
  let saving = $state(false)
  let completed = $state(true)

  async function getGoals(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_goals.json?goal_type=long_term_goal`
      )

      long_term_goal = resp.data.long_term_goal
      if (long_term_goal) {
        form.id = long_term_goal.id
        form.name = long_term_goal.name
        form.description = long_term_goal.description
        if (form.name?.length > 2) {
          form.name_entered = true
        }
      }
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your long term goal", "error")
    } finally {
      loaded = true
    }
  }

  getGoals()

  const initialForm = {
    id: null,
    name: "",
    name_entered: false,
    type: "LongTermGoal",
    description: "",
    obstacles_text: "",
    action_plan: "",
    parent_goal_id: rootGoalId,
    parent_goal_type: "RootGoal"
  }

  let form = $state({ ...initialForm })

  async function nextStep(): Promise<void> {
    saving = true
    errors = {}

    try {
      const resp = await xhr.post<{
        plan: PlanType
        onboarding: OnboardingType
      }>(`/plan_onboarding/${appStore.plan.id}/create_goal.json`, {
        goal: form
      })

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
      appStore.onboarding = resp.data.onboarding
    } catch (e: any) {
      console.error(e)
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  function setNameEntered() {
    if (nameInput.value.length > 2) {
      form.name_entered = true
    }
  }

  onMount(() => {
    if (form.name_entered !== true && nameInput) {
      nameInput.focus()
    }
  })
</script>

<h2 class="text-2xl text-center text-white">Guided Initial Goals Set Up</h2>
<h3 class="mt-2 text-xl font-bold text-center text-purple-400">
  <span class="text-2xl text-bold mr-1">1. </span>
  Add a Long Term Goal
</h3>

<div class="max-w-3xl mx-auto">
  <p class="mt-6 text-lg text-white">
    To get started with your Plan's Goals you begin with creating your Long Term
    Goals. Long Term Goals are realistic goals with a 3 - 5 year time horizon.
  </p>
  <p class="mt-2 text-lg text-white">
    Generally there are 4 - 6 Long Term Goals for a plan. Get started by
    creating your first one.
  </p>

  <div class="scale-75 mt-4">
    <ul class="bg-white w-full" style="transform: translateX(-100px)">
      <li>
        <Goal goalType="long_term_goal" name={form.name} />
      </li>
    </ul>
  </div>

  {#if loaded}
    <div class="space-y-8 mt-6 md:mt-4 max-w-lg">
      <div>
        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4">
          <div>
            <label for="name" class="text-white text-lg">Goal Name </label>
            <div class="mt-2 input-cont" class:error={errors.name}>
              <input
                type="text"
                placeholder="Goal name"
                bind:value={form.name}
                bind:this={nameInput}
                onblur={setNameEntered}
                class="input input-lg"
              />
              <div class="msg">{errors.name || ""}</div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-start gap-x-5">
          <Button
            onclick={nextStep}
            disabled={!completed}
            processing={saving}
            class="btn btn-rounded success btn-big flex items-center gap-1"
          >
            <span class="font-light text-core"> CREATE GOAL </span>
            <ChevronRight class="size-6 text-core" />
          </Button>
        </div>
      </div>
    </div>
  {/if}
</div>
